import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";

import { DocumentsService } from "../../../api/services/documents.service";
import {
  CreateDocumentDto,
  BulkCreateDocumentDto,
  UpdateDocumentDto,
  DeleteDocumentDto,
  FilterDocumentDto,
  DocumentStatsDto,
  LogDocumentDownloadDto,
  ResponseDocumentDto,
  DocumentStatsResponseDto,
  BulkOperationResultDto,
  DocumentOperationResponseDto,
} from "../../../api/models";
import { environment } from "../../../environments/environment";
import { LoggedUserService } from "@keus-template/auth";

const TENANT = environment.tenant;

@Injectable({ providedIn: "root" })
export class DocumentsApiService {
  private readonly _documentsService: DocumentsService =
    inject(DocumentsService);

  private readonly _loggedUserService: LoggedUserService =
    inject(LoggedUserService);

  private readonly _userId: string = this._loggedUserService.currentUser().id;
  private readonly _currentUser = this._loggedUserService.currentUser();

  /**
   * Create a new document
   */
  public create$(body: CreateDocumentDto): Observable<ResponseDocumentDto> {
    const createDto: CreateDocumentDto = { ...body, tenant: TENANT };
    return this._documentsService.documentsControllerCreateDocument({
      body: createDto,
    });
  }

  /**
   * Bulk create multiple documents
   */
  public bulkCreate$(
    documents: CreateDocumentDto[],
  ): Observable<BulkOperationResultDto> {
    const body: BulkCreateDocumentDto = { tenant: TENANT, documents };
    return this._documentsService.documentsControllerBulkCreateDocuments({
      body,
    });
  }

  /**
   * Filter/search documents
   */
  public filter$(
    filterDto: Partial<FilterDocumentDto & { documentType?: string }>,
  ): Observable<ResponseDocumentDto[]> {
    const body: FilterDocumentDto & { documentType?: string } = {
      ...filterDto,
      tenant: TENANT,
    };
    return this._documentsService.documentsControllerFilterDocuments({
      body: body as FilterDocumentDto,
    });
  }

  /**
   * Get a single document by ID
   */
  public findOne$(
    documentId: string,
    requestedBy?: string,
  ): Observable<ResponseDocumentDto> {
    return this._documentsService.documentsControllerGetDocument({
      tenant: TENANT,
      documentId,
      requestedBy,
    });
  }

  /**
   * Update an existing document
   */
  public update$(
    documentId: string,
    updateData: Partial<UpdateDocumentDto>,
  ): Observable<ResponseDocumentDto> {
    const body: UpdateDocumentDto = {
      ...updateData,
      tenant: TENANT,
      documentId,
      uploadedBy: updateData.uploadedBy || this._userId,
      uploadedByName: updateData.uploadedByName || this.getUserDisplayName(this._currentUser),
    };
    return this._documentsService.documentsControllerUpdateDocument({
      tenant: TENANT,
      documentId,
      body,
    });
  }

  /**
   * Delete a document
   */
  public delete$(
    documentId: string,
    deletedBy?: string,
    deleteS3File?: boolean,
    deletionReason?: string,
  ): Observable<DocumentOperationResponseDto> {
    const body: DeleteDocumentDto = {
      tenant: TENANT,
      documentId,
      deletedBy: deletedBy || this._userId,
      deleteS3File,
      deletionReason,
    };
    return this._documentsService.documentsControllerDeleteDocument({
      tenant: TENANT,
      documentId,
      body,
    });
  }

  /**
   * Get document statistics
   */
  public getStats$(
    statsDto: Partial<DocumentStatsDto>,
  ): Observable<DocumentStatsResponseDto> {
    const body: DocumentStatsDto = { ...statsDto, tenant: TENANT };
    return this._documentsService.documentsControllerGetDocumentStats({ body });
  }

  /**
   * Log document download
   */
  public logDownload$(
    documentId: string,
    requestedBy?: string,
    accessReason?: string,
  ): Observable<DocumentOperationResponseDto> {
    const body: LogDocumentDownloadDto = {
      tenant: TENANT,
      documentId,
      requestedBy: requestedBy || this._userId,
      accessReason,
    };
    return this._documentsService.documentsControllerLogDownload({
      tenant: TENANT,
      documentId,
      body,
    });
  }

  /**
   * Get display name for user
   */
  private getUserDisplayName(user: any): string {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.given_name && user.family_name) {
      return `${user.given_name} ${user.family_name}`;
    }
    return user.name || user.email || "Unknown User";
  }
}
